import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Shield, Save, Eye, EyeOff, Key, User, Mail, AlertTriangle, Clock, Monitor, Ban, UserCog, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSecurity() {
  const { token, user, setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPin, setShowPin] = useState(false);
  
  const [settings, setSettings] = useState({
    gate_pin: '',
    gate_pin_enabled: true
  });

  // Account section
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingAccount, setSavingAccount] = useState(false);
  const [activePins, setActivePins] = useState([]);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchPins();
    fetchAudit();
  }, []);

  const fetchAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/api/security/audit`, { headers: { Authorization: `Bearer ${token}` } });
      setAudit(res.data);
    } catch (e) {
      console.error('audit load failed', e);
    } finally {
      setAuditLoading(false);
    }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return String(d).slice(0, 16); }
  };

  const fetchPins = async () => {
    try {
      const res = await axios.get(`${API}/api/auth/active-pins`, { headers: { Authorization: `Bearer ${token}` } });
      setActivePins(res.data.pins || []);
    } catch {}
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/settings/platform`);
      setSettings({
        gate_pin: response.data.gate_pin || '',
        gate_pin_enabled: response.data.gate_pin_enabled !== false
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const WEAK_PINS = ['000000','111111','222222','333333','444444','555555','666666','777777','888888','999999','123456','654321','123123','112233','121212','696969','000001','100000'];

  const handleSave = async () => {
    if (settings.gate_pin_enabled && (!settings.gate_pin || settings.gate_pin.length !== 6)) {
      toast.error('PIN invalide', { description: 'Le PIN doit contenir exactement 6 chiffres' });
      return;
    }
    if (settings.gate_pin && !/^\d{6}$/.test(settings.gate_pin)) {
      toast.error('Format invalide', { description: 'Le PIN ne doit contenir que des chiffres' });
      return;
    }
    if (settings.gate_pin && WEAK_PINS.includes(settings.gate_pin)) {
      toast.error('PIN trop simple', { description: 'Ce code PIN est trop facile à deviner. Choisissez un PIN plus sécurisé.' });
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/platform`, {
        gate_pin: settings.gate_pin,
        gate_pin_enabled: settings.gate_pin_enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres de sécurité enregistrés');
    } catch (error) {
      toast.error('Erreur', { description: 'Impossible d\'enregistrer les paramètres' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              Sécurité
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              Gérez les paramètres de sécurité de la plateforme
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-security-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>

        {/* Gate PIN Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              PIN d'accès plateforme
            </CardTitle>
            <CardDescription>
              Ce PIN est demandé à tous les visiteurs avant d'accéder à la page de connexion.
              Les PIN clients fonctionnent également pour accéder à la plateforme.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Activer le PIN d'accès</Label>
                <p className="text-xs text-muted-foreground">
                  Si désactivé, les visiteurs accèdent directement à la page de connexion
                </p>
              </div>
              <Switch
                checked={settings.gate_pin_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, gate_pin_enabled: checked }))}
                data-testid="gate-pin-toggle"
              />
            </div>

            {/* PIN Input */}
            {settings.gate_pin_enabled && (
              <div className="space-y-2">
                <Label>PIN Plateforme (6 chiffres)</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showPin ? 'text' : 'password'}
                      value={settings.gate_pin}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setSettings(prev => ({ ...prev, gate_pin: value }));
                      }}
                      placeholder="123456"
                      maxLength={6}
                      className="pr-10 font-mono text-lg tracking-widest"
                      data-testid="gate-pin-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPin(!showPin)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const randomPin = Math.floor(100000 + Math.random() * 900000).toString();
                      setSettings(prev => ({ ...prev, gate_pin: randomPin }));
                    }}
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Générer
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Ce PIN peut être partagé avec vos clients pour accéder à la plateforme.
                  Chaque client peut aussi utiliser son propre PIN personnel.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Mon Compte */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Mon compte
            </CardTitle>
            <CardDescription>Modifier votre email de connexion et votre mot de passe</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Email de connexion</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder={user?.email || 'votre@email.com'}
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  data-testid="account-email-input"
                />
                <Button
                  variant="outline"
                  disabled={savingAccount || !newEmail || newEmail === user?.email}
                  onClick={async () => {
                    if (!newEmail) return;
                    setSavingAccount(true);
                    try {
                      await axios.put(`${API}/api/auth/update-email`, { new_email: newEmail }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      const updated = { ...user, email: newEmail };
                      setUser(updated);
                      localStorage.setItem('user', JSON.stringify(updated));
                      toast.success('Email modifié avec succès');
                      setNewEmail('');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Erreur lors de la modification');
                    } finally { setSavingAccount(false); }
                  }}
                  data-testid="account-email-save"
                >
                  <Mail className="w-4 h-4 mr-1" /> Modifier
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Email actuel : {user?.email}</p>
            </div>

            <div className="border-t pt-4 space-y-3">
              <Label>Changer le mot de passe</Label>
              <Input type="password" placeholder="Mot de passe actuel" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="account-current-pwd" />
              <Input type="password" placeholder="Nouveau mot de passe (min 6 caractères)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="account-new-pwd" />
              <Input type="password" placeholder="Confirmer le nouveau mot de passe" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="account-confirm-pwd" />
              <Button
                disabled={savingAccount || !currentPassword || !newPassword || !confirmPassword}
                onClick={async () => {
                  if (newPassword.length < 6) { toast.error('Le nouveau mot de passe doit faire au moins 6 caractères'); return; }
                  if (newPassword !== confirmPassword) { toast.error('Les mots de passe ne correspondent pas'); return; }
                  setSavingAccount(true);
                  try {
                    await axios.post(`${API}/api/auth/change-password`, {
                      current_password: currentPassword, new_password: newPassword
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success('Mot de passe modifié avec succès');
                    setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Erreur lors du changement de mot de passe');
                  } finally { setSavingAccount(false); }
                }}
                data-testid="account-pwd-save"
              >
                <Key className="w-4 h-4 mr-2" /> Changer le mot de passe
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Active PINs List */}
        {activePins.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Identifiants actifs ({activePins.length})
              </CardTitle>
              <CardDescription>Mots de passe et codes PIN de tous les utilisateurs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="active-pins-table">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th className="text-left py-2 px-3">Type</th>
                      <th className="text-left py-2 px-3">Nom</th>
                      <th className="text-left py-2 px-3">Email</th>
                      <th className="text-left py-2 px-3">Rôle</th>
                      <th className="text-left py-2 px-3">Mot de passe</th>
                      <th className="text-left py-2 px-3">Code PIN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activePins.map((p, i) => (
                      <tr key={i} className="border-b border-border/30 hover:bg-muted/20">
                        <td className="py-2 px-3">
                          <Badge variant={p.type === 'platform' ? 'default' : 'outline'} className="text-xs">
                            {p.type === 'platform' ? 'Plateforme' : 'Utilisateur'}
                          </Badge>
                        </td>
                        <td className="py-2 px-3 font-medium">{p.name}</td>
                        <td className="py-2 px-3 text-muted-foreground text-xs">{p.email || '—'}</td>
                        <td className="py-2 px-3 text-xs capitalize">{p.role === 'global' ? '—' : p.role?.replace('_', ' ')}</td>
                        <td className="py-2 px-3">
                          {p.password ? <code className="bg-muted px-2 py-1 rounded text-xs font-mono">{p.password}</code> : <span className="text-muted-foreground text-xs">—</span>}
                        </td>
                        <td className="py-2 px-3">
                          {p.pin ? <code className="bg-blue-500/10 text-blue-500 px-2 py-1 rounded text-xs font-mono">{p.pin}</code> : <span className="text-muted-foreground text-xs">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===================== AUDIT DE SÉCURITÉ ===================== */}
        <Card className="border-amber-500/30" data-testid="security-audit-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" /> Audit de sécurité
                </CardTitle>
                <CardDescription>Détecter une éventuelle intrusion : comptes à pouvoir, connexions, uploads, brute-force.</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchAudit} disabled={auditLoading} data-testid="refresh-audit-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${auditLoading ? 'animate-spin' : ''}`} /> Actualiser
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {!audit ? (
              <p className="text-sm text-muted-foreground">{auditLoading ? 'Chargement…' : 'Aucune donnée.'}</p>
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="audit-summary">
                  {[
                    { label: 'Comptes à pouvoir', value: audit.counts.privileged_accounts, icon: UserCog },
                    { label: 'Inscriptions (30j)', value: audit.counts.recent_registrations_30d, icon: User },
                    { label: 'Uploads suspects', value: audit.counts.suspicious_uploads, warn: audit.counts.suspicious_uploads > 0, icon: AlertTriangle },
                    { label: 'Blocages actifs', value: audit.counts.active_lockouts, icon: Ban },
                    { label: 'Utilisateurs', value: audit.counts.total_users, icon: User },
                  ].map((c) => (
                    <div key={c.label} className={`rounded-lg border p-3 ${c.warn ? 'border-red-500/40 bg-red-500/5' : 'border-border bg-muted/30'}`}>
                      <div className={`text-2xl font-bold ${c.warn ? 'text-red-500' : ''}`}>{c.value}</div>
                      <div className="text-xs text-muted-foreground">{c.label}</div>
                    </div>
                  ))}
                </div>

                {/* Privileged accounts */}
                <div>
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2"><UserCog className="w-4 h-4" /> Comptes administrateurs & analystes <span className="text-xs text-muted-foreground font-normal">(vérifiez que vous les reconnaissez tous)</span></h4>
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-sm" data-testid="privileged-accounts-table">
                      <thead><tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                        <th className="py-2 px-3">Rôle</th><th className="py-2 px-3">Email</th><th className="py-2 px-3">Créé le</th><th className="py-2 px-3">Statut</th>
                      </tr></thead>
                      <tbody>
                        {audit.privileged_accounts.map((a, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-2 px-3"><Badge variant="outline" className="capitalize text-xs">{(a.role || '').replace('_', ' ')}</Badge></td>
                            <td className="py-2 px-3 font-medium break-all">{a.email}</td>
                            <td className="py-2 px-3 text-xs text-muted-foreground">{fmtDate(a.created_at)}</td>
                            <td className="py-2 px-3">{a.is_online ? <Badge className="bg-green-500/15 text-green-600 text-xs">en ligne</Badge> : <span className="text-xs text-muted-foreground">{a.status || '—'}</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recent logins */}
                <div>
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2"><Clock className="w-4 h-4" /> Dernières connexions (date + IP + appareil)</h4>
                  <div className="overflow-x-auto rounded-lg border max-h-80 overflow-y-auto">
                    <table className="w-full text-sm" data-testid="recent-logins-table">
                      <thead><tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground sticky top-0">
                        <th className="py-2 px-3">Date</th><th className="py-2 px-3">Compte</th><th className="py-2 px-3">IP</th><th className="py-2 px-3">Appareil</th>
                      </tr></thead>
                      <tbody>
                        {audit.recent_logins.map((l, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-2 px-3 text-xs whitespace-nowrap">{fmtDate(l.login_at)}</td>
                            <td className="py-2 px-3 break-all">{l.email} <span className="text-xs text-muted-foreground capitalize">({(l.role || '').replace('_', ' ')})</span></td>
                            <td className="py-2 px-3 font-mono text-xs">{l.ip_address}</td>
                            <td className="py-2 px-3 text-xs flex items-center gap-1"><Monitor className="w-3 h-3" />{l.device_type} · {l.browser}</td>
                          </tr>
                        ))}
                        {audit.recent_logins.length === 0 && <tr><td colSpan="4" className="py-3 px-3 text-xs text-muted-foreground">Aucune connexion enregistrée.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Warnings: suspicious uploads + lockouts + recent regs */}
                {audit.suspicious_uploads.length > 0 && (
                  <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3" data-testid="suspicious-uploads">
                    <h4 className="font-medium text-sm text-red-500 flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4" /> Fichiers uploadés à risque</h4>
                    <ul className="text-xs space-y-1">{audit.suspicious_uploads.map((f, i) => <li key={i} className="font-mono">{f.filename} ({Math.round((f.size||0)/1024)} Ko)</li>)}</ul>
                  </div>
                )}
                {audit.recent_registrations.length > 0 && (
                  <div className="rounded-lg border p-3" data-testid="recent-registrations">
                    <h4 className="font-medium text-sm mb-1 flex items-center gap-2"><User className="w-4 h-4" /> Inscriptions des 30 derniers jours ({audit.recent_registrations.length})</h4>
                    <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">{audit.recent_registrations.map((u, i) => <li key={i}><span className="capitalize font-medium">{(u.role||'').replace('_',' ')}</span> — {u.email} <span className="text-muted-foreground">({fmtDate(u.created_at)})</span></li>)}</ul>
                  </div>
                )}
                {audit.pin_lockouts.length > 0 && (
                  <div className="rounded-lg border p-3" data-testid="pin-lockouts">
                    <h4 className="font-medium text-sm mb-1 flex items-center gap-2"><Ban className="w-4 h-4" /> Blocages anti-brute-force actifs ({audit.pin_lockouts.length})</h4>
                    <ul className="text-xs space-y-1">{audit.pin_lockouts.map((l, i) => <li key={i} className="font-mono">IP {l.ip} — {l.attempts} tentative(s){l.locked_until ? ` — bloqué jusqu'à ${fmtDate(l.locked_until)}` : ''}</li>)}</ul>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Key className="w-5 h-5 text-primary" />
              </div>
              <div className="space-y-1">
                <h4 className="font-medium">Comment ça fonctionne ?</h4>
                <p className="text-sm text-muted-foreground">
                  Lorsqu'un visiteur accède à votre plateforme, il doit d'abord entrer un code PIN valide.
                  Trois types de PIN sont acceptés :
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside mt-2 space-y-1">
                  <li><strong>PIN Plateforme :</strong> Le PIN défini ci-dessus, commun à tous</li>
                  <li><strong>PIN Client :</strong> Chaque client a son propre PIN personnel</li>
                  <li><strong>PIN Admin :</strong> Les administrateurs peuvent aussi définir leur PIN</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
